import clsx from "clsx";
import { AnimatePresence, motion } from "framer-motion";
import React, { useEffect, type JSX } from "react";
import { MdOutlineClose } from "react-icons/md";

interface Props {
  isOpen: boolean;
  onClose?: () => void;
  hasCloseButton?: boolean;
  hasOverlay?: boolean;
  isOverlayCloseable?: boolean;
  title?: string;
  titleSecondLine?: string;
  children: React.ReactNode;
  className?: string;
}

export const ModalBase = ({
  children,
  className,
  isOpen,
  onClose,
  hasCloseButton,
  hasOverlay = true,
  isOverlayCloseable = true,
  title,
  titleSecondLine,
}: Props): JSX.Element => {
  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "auto";

    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isOpen]);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className={"fixed left-0 top-0 z-50 flex h-full w-full items-center justify-center backdrop-blur-sm"}
        >
          <div
            role="none"
            className={clsx(["absolute h-full w-full", { "bg-base-black opacity-20 blur-sm": hasOverlay }])}
            onClick={isOverlayCloseable ? onClose : undefined}
          ></div>

          <motion.div
            initial={{ y: "25%" }}
            animate={{ y: "0%" }}
            exit={{ y: "25%" }}
            className={clsx([
              "mini-scrollbar shadow-box-primary relative z-50 m-3 w-full max-w-[40rem]",
              "rounded-lg bg-base-white px-3 py-5 lg:max-h-[90vh] lg:max-w-[52.5rem] lg:p-10 max-w-[80%]",
              className,
            ])}
          >
            {hasCloseButton && (
              <MdOutlineClose
                className="text-primary absolute right-3 top-3 cursor-pointer"
                size={24}
                onClick={onClose}
              />
            )}

            {!!title && (
              <h2 className={clsx(["whitespace-pre-line text-center", { "mb-3": !titleSecondLine }])}>{title}</h2>
            )}
            {!!titleSecondLine && <h2 className="mb-3 whitespace-pre-line text-center">{titleSecondLine}</h2>}

            {children}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
